import React, { useState } from "react";
import { ChevronDown, ChevronUp, BarChart3, TrendingUp } from "lucide-react";
import ExpenseBreakdownChart from "../charts/ExpenseBreakdownChart";
import RevenueBreakdownChart from "../charts/RevenueBreakdownChart";
import ExpenseTypeBreakdownChart from "../charts/ExpenseTypeBreakdownChart";
import CashFlowHealthChart from "../charts/CashFlowHealthChart";
import CashFlowBarChart from "../charts/CashFlowBarChart";
import RunwayProjectionChart from "../charts/RunwayProjectionChart";

interface MobileChartsProps {
  data: any;
  visibleDateRange: { startDate: Date; endDate: Date };
  events?: any[]; // Timeline events for asset calculations
  currentSnapshot?: any; // Timeline snapshot to get current date and processed data
}

type ChartSection = "cashflow" | "expenses" | "revenues" | "expense-types" | "cash-health" | "runway";

export const MobileCharts: React.FC<MobileChartsProps> = ({
  data,
  visibleDateRange,
  events = [],
  currentSnapshot,
}) => {
  const [expandedSection, setExpandedSection] = useState<ChartSection | null>(null);

  // Use currentSnapshot date if available for timeline-aware breakdown charts
  const breakdownDate = currentSnapshot?.date || new Date();

  const toggleSection = (section: ChartSection) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const ChartSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    section: ChartSection;
    children: React.ReactNode;
  }> = ({ title, icon, section, children }) => {
    const isExpanded = expandedSection === section;
    
    return (
      <div className="bg-surface border-b border-white/10 last:border-b-0">
        <button
          onClick={() => toggleSection(section)}
          className="w-full px-4 py-4 flex items-center justify-between hover:bg-surface-elevated transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="text-white/65 flex-shrink-0">{icon}</div>
            <span className="font-semibold text-white text-left">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            {isExpanded && (
              <span className="text-xs text-white/45 font-medium">Tap to collapse</span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-white/45" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white/45" />
            )}
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-6 bg-surface-elevated">
            <div className="bg-surface rounded-lg p-4 shadow-none border border-white/10 overflow-hidden">
              {children}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="lg:hidden">
      <ChartSection
        title="Cash Flow Overview"
        icon={<BarChart3 className="h-5 w-5" />}
        section="cashflow"
      >
        <div className="min-h-48">
          <CashFlowBarChart
            data={data}
            visibleDateRange={visibleDateRange}
            currentDate={breakdownDate}
            height={240}
          />
        </div>
      </ChartSection>

      <ChartSection
        title="Expense Breakdown"
        icon={<div className="w-5 h-5 bg-danger rounded-full"></div>}
        section="expenses"
      >
        <div className="h-80">
          <ExpenseBreakdownChart
            expenses={(currentSnapshot?.activeExpenses || []).map((e: any) => ({
              ...e,
              id: e.id,
              name: e.name,
              monthlyAmount: e.monthlyAmount ?? e.oneTimeAmount ?? 0,
              category: e.category,
              startDate: e.startDate,
              endDate: e.endDate,
              frequency: e.frequency,
              isRecurring: e.isRecurring,
            }))}
            events={events}
            currentDate={breakdownDate}
          />
        </div>
      </ChartSection>

      <ChartSection
        title="Revenue Breakdown"
        icon={<div className="w-5 h-5 bg-green-500 rounded-full"></div>}
        section="revenues"
      >
        <div className="h-80">
          <RevenueBreakdownChart
            revenues={(currentSnapshot?.activeRevenues || []).map((r: any) => ({
              ...r,
              id: r.id,
              name: r.name,
              monthlyAmount: r.monthlyAmount ?? r.amount ?? 0,
              type: (r.type ?? r.category) as any,
              startDate: r.startDate,
              endDate: r.endDate,
              frequency: r.frequency,
              isRecurring: r.isRecurring,
            }))}
            events={events}
            currentDate={breakdownDate}
          />
        </div>
      </ChartSection>

      <ChartSection
        title="Expense Types"
        icon={<div className="w-5 h-5 bg-brand rounded-full"></div>}
        section="expense-types"
      >
        <div className="h-80">
          <ExpenseTypeBreakdownChart
            expenses={(currentSnapshot?.activeExpenses || []).map((e: any) => ({
              ...e,
              expenseType: e.expenseType || "fixed",
              id: e.id,
              name: e.name,
              monthlyAmount: e.monthlyAmount ?? e.oneTimeAmount ?? 0,
              category: e.category,
            }))}
            currentDate={breakdownDate}
          />
        </div>
      </ChartSection>

      <ChartSection
        title="Cash Flow Health"
        icon={<div className="w-5 h-5 bg-purple-500 rounded-full"></div>}
        section="cash-health"
      >
        <div className="h-80">
          <CashFlowHealthChart
            currentSnapshot={currentSnapshot}
            currentDate={breakdownDate}
          />
        </div>
      </ChartSection>

      <ChartSection
        title="Runway Projection"
        icon={<TrendingUp className="h-5 w-5" />}
        section="runway"
      >
        <div className="min-h-48">
          <RunwayProjectionChart
            data={data}
            visibleDateRange={visibleDateRange}
            height={250}
          />
        </div>
      </ChartSection>
    </div>
  );
};
